import { useCallback, useMemo, useState, useEffect, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
// Behavioural controls (popovers / form inputs) use the project ui kit — the visual redesign (table, tags,
// buttons, icons) is Tailwind, matching the TwDataTable-migrated pages (Stock, Products).
import { Select, DatePicker, Tooltip, Checkbox, useApp, Popconfirm, StatCard, Tag, cn, Modal, Textarea, Alert, Input, InputNumber, CodeChip } from '../../ui';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUrlBool, useUrlString, useUrlDate, useUrlNumber } from '../../hooks/useUrlState';
import {
  Plus, Eye, CheckCircle2, Package, Truck, Flag, FileCheck2, Check, Ban, ArrowLeftRight, Wallet, Download,
  ClipboardList, Stamp, X, Store, IndianRupee, Clock, Search, Upload,
} from 'lucide-react';
import dayjs, { Dayjs } from 'dayjs';
import { disableFuture } from '../../lib/dateRange';
import { request } from '../../api/client';
import { requestAllPages } from '../../api/allPages';
import { usePermission, useAuthStore, useHasRole } from '../../store/auth';
import { TwDataTable, Pill, type DataTableColumn } from '../../components/TwDataTable';
import { RetailerLink, UserLink, OrderLink, PersonLink } from '../../components/EntityLinks';
import { RoleBadge } from '../../components/RoleBadge';
import { TerritoryChips } from '../../components/TerritoryChips';
import { ChannelTag, CHANNELS } from '../../components/ChannelTag';
import { entityHue } from '../../ui/EntityAvatar';
import { useDebounce } from '../../hooks/useDebounce';
import { useRetailerSearch } from '../../hooks/useRetailerSearch';
import { Button } from '../../ui/Button';
import { formatIst, formatIstDate, formatIstTime } from '../../lib/time';
import { fmtQty, variantText, isRedundantSku } from '../../lib/format';
import { orderStatusLabel, orderTimelineStep } from '../../lib/statusTone';
import { boxLoosePhrase } from '../../lib/packaging';
import { useServerSort } from '../../hooks/useServerSort';
import { ExportDialog } from '../../components/ExportDialog';
import { ApproveOrderModal } from './ApproveOrderModal';
import { PayOrderModal } from './PayOrderModal';
import { inr, inr2 } from '../../lib/format';

// Role labels + colours come from components/RoleBadge — one palette for the whole app. This page
// used to keep its own label map and render the role in a NEUTRAL pill, which is why every capsule
// here was grey while the same person's role showed green or purple on 23 other screens.
// Status → Pill colour (the Tailwind palette: default/green/blue/gold/orange/red/purple/lime).
const STATUS_PILL: Record<string, string> = {
  DRAFT: 'default', PLACED: 'blue', AWAITING_PRODUCTION: 'purple', IN_PRODUCTION: 'purple',
// IN FULFILLMENT is PURPLE everywhere. It was lime here and plum on the WHM dashboard, so the
// same stage changed colour depending on which screen you opened it from.
  AWAITING_TRANSFER: 'blue', APPROVED: 'blue', ACCEPTED: 'purple', PACKED: 'gold', DISPATCHED: 'orange',
  DELIVERED: 'green', INVOICED: 'purple', CANCELLED: 'red', CLOSED: 'default',
  ON_HOLD: 'orange', CORRECTION_REQUIRED: 'gold', REJECTED: 'red', BACKORDER: 'orange',
};
// Timeline dot colour per status (Tailwind bg-*).
const DOT: Record<string, string> = {
  DRAFT: 'bg-line-strong', PLACED: 'bg-sky-500', AWAITING_PRODUCTION: 'bg-violet-500', IN_PRODUCTION: 'bg-violet-500',
  AWAITING_TRANSFER: 'bg-sky-500', APPROVED: 'bg-sky-500', ACCEPTED: 'bg-violet-500', PACKED: 'bg-amber-500',
  DISPATCHED: 'bg-orange-500', DELIVERED: 'bg-brand-500', INVOICED: 'bg-violet-500', CANCELLED: 'bg-red-500', CLOSED: 'bg-line-strong',
};

// Statuses offered in the list filter — the live workflow, in flow order. Excludes DRAFT (orders are
// created PLACED, never DRAFT) and the retired production states.
const FILTER_STATUSES = [
  'PLACED', 'ON_HOLD', 'CORRECTION_REQUIRED', 'APPROVED', 'ACCEPTED',
  'AWAITING_TRANSFER', 'BACKORDER', 'PACKED', 'DISPATCHED', 'DELIVERED',
  'INVOICED', 'CLOSED', 'CANCELLED', 'REJECTED',
];

/**
 * The four lanes the KPI band puts on screen, in workflow order — every one a queue somebody has to
 * clear, which is why DELIVERED and CANCELLED are absent: they are outcomes, not work.
 *
 * One status each, deliberately. A card grouping (say) APPROVED + ACCEPTED could not be reproduced by
 * a click, because the status filter holds a single value — the table would narrow to something other
 * than the number on the card.
 *
 * PACKED is the one status split in two, because it holds two different queues: goods that cannot leave
 * until Accounts uploads their Tally invoice, and goods the warehouse can dispatch. Both halves are
 * server-side (`lane`), so each card's count is its pager, and together they are every PACKED order.
 */
const KPI_LANES: { status: string; lane?: 'to-invoice' | 'to-dispatch'; label: string; tone: 'sky' | 'gold' | 'warning' | 'brand' | 'success'; icon: JSX.Element }[] = [
  { status: 'PLACED', label: 'Awaiting approval', tone: 'sky', icon: <Stamp className="size-4" /> },
  { status: 'ACCEPTED', label: 'To pack', tone: 'gold', icon: <Package className="size-4" /> },
  { status: 'PACKED', lane: 'to-invoice', label: 'To invoice', tone: 'warning', icon: <Upload className="size-4" /> },
  { status: 'PACKED', lane: 'to-dispatch', label: 'To dispatch', tone: 'success', icon: <FileCheck2 className="size-4" /> },
  { status: 'DISPATCHED', label: 'In transit', tone: 'brand', icon: <Truck className="size-4" /> },
];

/**
 * Rows per page on first open. The register pages against the SERVER (`page`/`pageSize` → SQL
 * LIMIT), so this is the size of one request, not a ceiling on what the list can reach.
 *
 * It used to be a 200-row CAP: the page asked for the latest 200 orders and paged through them in
 * the browser, so a register of 302 ended at "200 rows" across 8 pages with the other 102 simply
 * unreachable — the KPI card had to apologise for the table ("list shows the latest 200") because
 * the two could not agree. Paging on the server makes the pager's total the register's real size.
 */
const DEFAULT_PAGE_SIZE = 25;

// A non-admin (retailer / sales person / sales head) can cancel an order only in these pre-dispatch
// states. Once DISPATCHED, only an admin (ORDER.EDIT) may cancel.
const PRE_DISPATCH_CANCELLABLE = ['PLACED', 'ON_HOLD', 'CORRECTION_REQUIRED', 'APPROVED', 'ACCEPTED', 'AWAITING_TRANSFER', 'BACKORDER', 'PACKED'];

/*
 * Cancelling from one of these needs a REASON — the warehouse has been routed the work and a proforma
 * is out, so the server refuses a bare cancel (CANCEL_REASON_REQUIRED), and for anyone who cannot decide
 * a cancellation it is the text the Super Admin rules on. The row's Cancel sent no reason at all, so on
 * these statuses it could only ever fail; it now asks first. Mirrors order.service.ts.
 */
const CANCEL_NEEDS_REASON = ['APPROVED', 'ACCEPTED', 'AWAITING_TRANSFER', 'BACKORDER', 'PACKED', 'DISPATCHED'];

/**
 * The order the row actions are laid out in — and the reason they are SORTED rather than pushed in
 * whatever sequence the status branches happen to run.
 *
 * With icon-only buttons, POSITION is half the label: if "Cancel" is third on a PLACED row and first
 * on an IN_PRODUCTION one, muscle memory sends the click to the wrong icon. So every action has a
 * fixed rank and rows only ever *omit* actions, never reshuffle them. The ranking itself follows the
 * order's own life: money first (Pay), then whatever moves it one step forward, then the ways to
 * stop it, and finally the passive View — destructive last so the eye never lands on it first.
 */
const ACTION_ORDER: Record<string, number> = {
  pay: 10, approve: 20, pick: 30, dispatch: 40, deliver: 50, invoice: 60, close: 70,
  cancelConsolidation: 80, cancel: 90,
};

// Shared with the order detail page (lib/statusTone) so the register, the filter dropdown and the
// order's own header cannot disagree about what a status is called.
const statusLabel = orderStatusLabel;
// Exact-paise money (order/invoice/payment amounts): round-off was removed, so totals carry paise
// (e.g. ₹9,080.10) — showing them rounded would hide the paise and mismatch the stored value.

// Effective discount = the reduction from gross `subtotal` down to the net `taxableAmt` (mirrors
// OrderDetailPage). This captures a LOCKED advance cash-discount, which lowers taxableAmt/tax/grandTotal
// but leaves subtotal + discountAmt gross — so raw discountAmt (often 0) makes Subtotal−Discount+Tax NOT
// equal the Grand total shown. Falls back to raw discountAmt when taxableAmt isn't set.
const effectiveDiscount = (o: any): number =>
  Number(o.taxableAmt) > 0 ? Math.max(0, Number(o.subtotal ?? 0) - Number(o.taxableAmt ?? 0)) : Number(o.discountAmt ?? 0);

const TH = 'px-3 py-2.5 text-left type-overline text-muted';
const TD = 'px-3 py-2.5 align-top';

// ---- Expanded row: order details + status timeline ----
function OrderExpand({ order, retailer, salesPersonName }: { order: any; retailer?: any; salesPersonName?: string }) {
  const { data: timeline, isLoading: tlLoading } = useQuery({
    queryKey: ['order-timeline', order.id],
    queryFn: () => request<any[]>({ url: `/orders/${order.id}/timeline` }),
  });
  const { data: detail } = useQuery({
    queryKey: ['order-detail', order.id],
    queryFn: () => request<any>({ url: `/orders/${order.id}` }),
  });
  // Shipments carry the picked batch numbers per order line (populated once packed) — the same source the
  // order-detail Items tab uses, so the expanded row can show "Allocated batches" too instead of dropping it.
  const { data: shipments } = useQuery({
    queryKey: ['order-shipments', order.id],
    queryFn: () => request<any[]>({ url: `/orders/${order.id}/shipments` }),
  });

  const tax = Number(order.cgstAmt ?? 0) + Number(order.sgstAmt ?? 0) + Number(order.igstAmt ?? 0);
  const items = detail?.items ?? [];
  // Allocated batches per order line, from non-cancelled shipments. batchNo is staff-only (the API omits it
  // for retailer/farmer logins), so this naturally shows nothing to customer roles.
  const batchesByLine = useMemo(() => {
    const m = new Map<string, { batchNo: string; qty: number; expiry?: string | null }[]>();
    for (const s of (shipments ?? [])) {
      if (s.status === 'CANCELLED') continue;
      for (const li of (s.items ?? [])) {
        if (!li.batchNo) continue;
        const key = String(li.orderItemId);
        const arr = m.get(key) ?? [];
        arr.push({ batchNo: li.batchNo, qty: Number(li.qty), expiry: li.expiry });
        m.set(key, arr);
      }
    }
    return m;
  }, [shipments]);

  // Live stock per ordered product (across warehouses), so each line shows whether it can actually be
  // fulfilled, the available quantity, and from which warehouse(s).
  /*
   * Staff only. A dealer holds STOCK.VIEW *and* ORDER.VIEW, so without the identity check this
   * panel asked for — and was served — the company's stock in every warehouse on the dealer's own
   * order. The server now refuses it too; this stops the page asking a question it must not ask.
   */
  const isCustomer = useHasRole('RETAILER', 'FARMER');
  const canViewStock = usePermission('STOCK.VIEW') && !isCustomer;
  const productIds = useMemo(
    () => [...new Set(items.map((i: any) => String(i.productId)).filter(Boolean))],
    [items],
  );
  const { data: stock } = useQuery({
    queryKey: ['order-stock', order.id, productIds],
    queryFn: () => request<Record<string, { warehouses: Record<string, number>; total: number }>>({
      url: '/stock/by-product', params: { productIds: productIds.join(',') },
    }),
    enabled: canViewStock && productIds.length > 0,
  });

  /*
   * Label ABOVE value, not across the card from it.
   *
   * These were `justify-between` rows, so "Retailer" sat at the far left and its value at the far
   * right with a hand-span of empty tint between them — the reader had to track across the gap to
   * pair them, and a missing value left a lone em dash stranded in the middle of nowhere. Stacked,
   * the pair is one object. Same shape as the order detail page's OField.
   */
  const Detail = ({ label, children }: { label: string; children: any }) => (
    <div className="min-w-0">
      <div className="type-overline text-subtle">{label}</div>
      <div className="mt-0.5 min-w-0 type-body text-content">{children}</div>
    </div>
  );

  /* An invoice line: label left, figure right in tabular numerals so the column reads down. */
  const Money = ({ label, value, tone, strong }: { label: string; value: any; tone?: 'brand'; strong?: boolean }) => (
    <div className={cn('flex items-baseline justify-between gap-3 py-1', strong && 'border-t border-line pt-2')}>
      <span className={cn(strong ? 'type-body-strong text-content' : 'type-caption text-subtle')}>{label}</span>
      <span className={cn('tabular-nums',
        strong ? 'type-metric-sm text-content'
          : tone === 'brand' ? 'type-body text-brand-700 dark:text-brand-300'
            : 'type-body text-content')}>{value}</span>
    </div>
  );

  return (
    <div className="grid grid-cols-1 gap-5 p-4 lg:grid-cols-[3fr_2fr]">
      {/* Left — who the order is for, and what is on it */}
      <div className="space-y-5">
        {/*
          * THREE sections, not one flat list.
          *
          * Everything used to sit in a single label/value run: the dealer's phone number, the
          * channel, and the grand total were the same size, the same colour and the same distance
          * apart, so the panel had no shape and the one figure that matters was just row nine. The
          * facts are now grouped by the question they answer — WHO, HOW it ships, WHAT is on it —
          * each under its own tinted mark, and the money has moved out into its own panel opposite.
          */}
        <XSection icon={<Store className="size-4" />} tone="brand" title="Parties">
          <div className="grid grid-cols-1 gap-x-6 gap-y-3 rounded-card border border-line bg-surface p-3 shadow-e1 sm:grid-cols-2">
            <Detail label="Retailer">
              <RetailerLink id={order.retailerId} className="font-semibold">
                {retailer?.companyName ?? detail?.retailerName ?? order.companyName ?? `#${order.retailerId}`}
                {retailer?.code ? <span className="font-normal text-subtle"> ({retailer.code})</span> : null}
              </RetailerLink>
            </Detail>
            <Detail label="Contact">{retailer?.contactPerson ? <>{retailer.contactPerson} · </> : ''}{retailer?.mobile ?? '—'}{retailer?.city ? ` · ${retailer.city}` : ''}</Detail>
            {/* The address goes HERE rather than in the row above, on purpose: that cell is one line
                by design (its height is governed by the density control), and chips wrap. The
                expanded row is already where the dealer's context lives. */}
            <Detail label="Territory"><TerritoryChips territoryId={order.retailerTerritoryId ?? retailer?.territoryId} city={retailer?.city} /></Detail>
            {/* Both names carry the id of the person they name — without one the link could only
                reach the bare staff list, which is not "the page about them". */}
            <Detail label="Placed by">{order.placedByName ? <span className="inline-flex items-center gap-1.5"><PersonLink id={order.createdBy} role={order.placedByRole} retailerId={order.retailerId}>{order.placedByName}</PersonLink><RoleBadge role={order.placedByRole} /></span> : '—'}</Detail>
            {/* Badged like "Placed by" beside it: a Sales Head owns beats too, so the name alone
                does not say which of the two field roles this person holds. */}
            <Detail label="Sales person">{(salesPersonName ?? detail?.salesPersonName)
              ? <span className="inline-flex items-center gap-1.5">
                  <UserLink id={order.salesPersonId}>{salesPersonName ?? detail?.salesPersonName}</UserLink>
                  <RoleBadge role={order.salesPersonRole ?? detail?.salesPersonRole} />
                </span>
              : (order.salesPersonId ? `#${order.salesPersonId}` : '—')}</Detail>
          </div>
        </XSection>

        <XSection icon={<Truck className="size-4" />} tone="sky" title="Fulfilment">
          <div className="grid grid-cols-1 gap-x-6 gap-y-3 rounded-card border border-line bg-surface p-3 shadow-e1 sm:grid-cols-2">
            {/* The same pill the order's own page shows — the route, with a glyph that varies by
                channel, instead of the raw enum in caps. */}
            <Detail label="Channel"><ChannelTag channel={order.channel} /></Detail>
            <Detail label="Warehouse">
              {detail?.dispatchWarehouseName ?? <span className="text-subtle">Assigned at packing</span>}
            </Detail>
          </div>
        </XSection>

        <XSection
          icon={<Package className="size-4" />} tone="violet" title="Products"
          extra={items.length ? <span className="type-caption text-subtle">{items.length} product{items.length === 1 ? '' : 's'}</span> : undefined}
        >
        <div className="overflow-x-auto rounded-card border border-line bg-surface shadow-e1">
          <table className="w-full border-collapse text-sm">
            <thead className="gb-thead-tint bg-surface-2/80">
              <tr>
                <th className={TH}>Product</th>
                <th className={`${TH} text-right`}>Qty</th>
                {canViewStock && <th className={TH}>In stock</th>}
                <th className={`${TH} text-right`}>Rate</th>
                <th className={`${TH} text-right`}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={canViewStock ? 5 : 4} className="px-3 py-6 text-center text-subtle">No products on this order</td></tr>
              ) : items.map((it: any) => {
                const s = stock?.[String(it.productId)];
                const total = Number(s?.total ?? 0);
                const ordered = Number(it.qty ?? 0);
                const partial = total > 0 && total < ordered;
                const color = total <= 0 ? 'red' : partial ? 'orange' : 'green';
                const label = total <= 0 ? 'Out of stock' : partial ? 'Partial' : 'In stock';
                const whs = s ? Object.entries(s.warehouses) : [];
                // Derive the variant label from pack size + unit (matches the catalog: "500 ml"),
                // falling back to the stored variantLabel, so it reads consistently everywhere.
                const variant = variantText(it.packSize, it.uom, it.variantLabel);
                return (
                  <tr key={it.id} className="border-t border-line">
                    <td className={TD}>
                      <div>
                        {it.productNameSnapshot}{variant ? ` ${variant}` : ''}
                        {it.sku && !isRedundantSku(it.sku, `${it.productNameSnapshot} ${variant}`.trim(), it.productNameSnapshot) ? <span className="text-xs text-subtle"> · {it.sku}</span> : null}
                      </div>
                      {(() => {
                        // Show the picked batch(es) once the line has been packed (same as the detail Items tab).
                        const allocs = batchesByLine.get(String(it.id)) ?? [];
                        if (!allocs.length) return null;
                        return (
                          <div className="mt-1">
                            <div className="text-2xs text-subtle">Allocated batches</div>
                            {allocs.map((a, i) => (
                              <div key={i} className="flex items-center gap-2 leading-[1.9]">
                                <span className="font-mono text-xs">{a.batchNo}</span>
                                <Pill color="blue">×{fmtQty(a.qty)}</Pill>
                                {a.expiry && <span className="whitespace-nowrap text-2xs text-subtle">exp {formatIstDate(a.expiry)}</span>}
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </td>
                    <td className={`${TD} text-right`}>
                      {fmtQty(it.qty)}
                      {(() => {
                        // box/loose split for a packed line (same as the detail Items tab) — total units unchanged.
                        const phrase = boxLoosePhrase(it.boxQuantity, it.looseUnitQuantity, it.unitsPerBoxSnapshot);
                        return phrase ? <div className="text-2xs text-subtle">{phrase}</div> : null;
                      })()}
                    </td>
                    {canViewStock && (
                      <td className={TD}>
                        {!stock ? <span className="text-subtle">—</span> : (
                          <div className="flex flex-col gap-1">
                            <span className="inline-flex flex-wrap items-center gap-1">
                              <Pill color={color}>{label}</Pill>
                              {partial && <Pill color="red">Short by {fmtQty(ordered - total)}</Pill>}
                              <span className="text-muted">{partial ? `${fmtQty(total)} / ${fmtQty(ordered)}` : `${fmtQty(total)} avail`}</span>
                            </span>
                            {whs.length > 0 && (
                              <span className="inline-flex flex-wrap items-center gap-1">
                                {whs.map(([code, q]) => <Pill key={code} color="blue">{code}: {fmtQty(q as number)}</Pill>)}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                    )}
                    <td className={`${TD} text-right`}>{inr2(it.unitPrice)}</td>
                    <td className={`${TD} text-right`}><b>{inr2(it.lineTotal)}</b></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </XSection>
      </div>

      {/* Right — the money, then how it got here */}
      <div className="space-y-5">
        {/*
          * Money in ITS OWN panel, as an invoice reads: the parts stacked, the payable figure last
          * and largest above a rule. In the flat list it used to live in, "Grand total" was one more
          * grey row and the reader had to hunt for the number the whole row is about.
          */}
        <XSection icon={<IndianRupee className="size-4" />} tone="gold" title="Money">
          <div className="rounded-card border border-line bg-surface px-3 py-2.5 shadow-e1">
            <Money label="Subtotal" value={inr2(order.subtotal)} />
            <Money label="Discount" value={inr2(effectiveDiscount(order))} />
            <Money label="Tax" value={inr2(tax)} />
            {Math.abs(Number(order.roundOff ?? 0)) >= 0.005 && (
              <Money label="Round off" value={`${Number(order.roundOff) < 0 ? '−' : '+'}${inr2(Math.abs(Number(order.roundOff)))}`} />
            )}
            <Money label="Grand total" value={inr2(order.grandTotal)} strong />
            {/* An ESTIMATED advance CD leaves the OFFICIAL totals gross on purpose (the offer lives in
                net_*), so this panel used to say "Discount ₹0 · Grand total ₹9,614" directly beneath a
                row cell reading ₹8,653 — the same order, two totals, no explanation. Spell the offer
                out here so the two agree. */}
            {order.advanceCdStatus === 'ESTIMATED' && Number(order.netGrandTotal) > 0 && (
              <div className="mt-2 rounded-control border border-brand-500/25 bg-brand-500/[0.07] px-2.5 py-1.5">
                <Money label={`If prepaid — advance CD ${Number(order.advanceCdPct)}%`} value={`− ${inr2(order.advanceCdAmount)}`} tone="brand" />
                <Money label="Net payable if prepaid" value={inr2(order.netGrandTotal)} tone="brand" />
              </div>
            )}
            {order.advanceCdStatus === 'LOCKED' && Number(order.netGrandTotal) > 0 && (
              <div className="mt-2 rounded-control border border-brand-500/25 bg-brand-500/[0.07] px-2.5 py-1.5">
                <Money label={`Cash discount locked (${Number(order.advanceCdPct)}%)`} value={inr2(order.netGrandTotal)} tone="brand" />
              </div>
            )}
          </div>
        </XSection>

        <XSection icon={<Clock className="size-4" />} tone="neutral" title="Status timeline">
        <div className="rounded-card border border-line bg-surface px-3 py-3 shadow-e1">
          {tlLoading ? (
            <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-8 animate-pulse rounded bg-surface-3" />)}</div>
          ) : (timeline ?? []).length === 0 ? (
            <div className="py-6 text-center text-sm text-subtle">No history yet</div>
          ) : (
            <ol className="relative ml-1 space-y-4 border-l border-line pl-5">
              {(timeline ?? []).map((t: any) => (
                <li key={t.id} className="relative">
                  <span className={`absolute -left-6.25 top-1 h-2.5 w-2.5 rounded-full ring-2 ring-surface ${DOT[orderTimelineStep(t).step] ?? 'bg-line-strong'}`} />
                  {/* A shipment step that left the order where it was (part-shipped stays PACKED) is named as
                      that step, not as the order's unchanged status (DEF-062). */}
                  <div className="type-body-strong text-content">
                    {orderTimelineStep(t).partialShipment
                      ? (orderTimelineStep(t).step === 'DISPATCHED' ? 'Partially dispatched' : 'Partially delivered')
                      : statusLabel(t.toStatus)}
                    {t.fromStatus && !orderTimelineStep(t).partialShipment && <span className="font-normal text-subtle"> (from {statusLabel(t.fromStatus)})</span>}
                  </div>
                  {/* Who moved it, badged with the role they held at the time — the endpoint already
                      sends `changedByRoleCode` for exactly this. A timeline that names people without
                      saying what they are makes the reader guess whether a step was taken by the rep,
                      the warehouse or an admin. */}
                  <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-muted">
                    <span>{formatIst(t.at)} · {t.changedByName ?? (t.changedBy ? `#${t.changedBy}` : 'system')}</span>
                    {t.changedByRoleCode && <RoleBadge role={t.changedByRoleCode} />}
                    {t.warehouseName && <span>· {t.warehouseName}</span>}
                  </div>
                  {t.notes && <div className="mt-0.5 text-xs text-subtle">{t.notes}</div>}
                </li>
              ))}
            </ol>
          )}
        </div>
        </XSection>
      </div>
    </div>
  );
}

/**
 * One titled block inside an expanded row.
 *
 * The panel had a single bold line ("Order details") over eleven identical grey rows, so nothing
 * said where one subject ended and the next began. A tinted mark plus a title gives every group the
 * two visible levels the app's detail pages already use, and the hue is what makes them scannable
 * at a glance rather than a wall of label/value pairs.
 */
const XSECTION_CHIP: Record<string, string> = {
  brand: 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300',
  sky: 'bg-sky-500/12 text-sky-600 dark:text-sky-400',
  gold: 'bg-harvest-50 text-harvest-700 dark:bg-harvest-400/15 dark:text-harvest-300',
  violet: 'bg-violet-500/12 text-violet-600 dark:text-violet-400',
  neutral: 'bg-surface-3 text-muted',
};

function XSection({ icon, tone, title, extra, children }: {
  icon: ReactNode; tone: keyof typeof XSECTION_CHIP; title: string; extra?: ReactNode; children: ReactNode;
}) {
  return (
    <section>
      <div className="mb-2 flex items-center gap-2">
        <span className={cn('grid size-7 shrink-0 place-items-center rounded-card', XSECTION_CHIP[tone])} aria-hidden>{icon}</span>
        <h4 className="type-label-strong text-content">{title}</h4>
        {extra && <span className="ml-auto">{extra}</span>}
      </div>
      {children}
    </section>
  );
}

export function OrdersListPage() {
  const navigate = useNavigate();
  const canViewUsers = usePermission('USER.VIEW');
  const canViewRetailers = usePermission('RETAILER.VIEW');
  const canCreateOrder = usePermission('ORDER.CREATE');
  // Matches the server's ORDER.EXPORT gate — ORDER.VIEW is held by every sales role and by retailers.
  const canExportOrders = usePermission('ORDER.EXPORT');
  const [exportOpen, setExportOpen] = useState(false);
  // PATCH /orders/:id/status is gated by ORDER.APPROVE OR ORDER.EDIT (mirrors the backend).
  const canChangeStatus = usePermission('ORDER.APPROVE') || usePermission('ORDER.EDIT');
  // Fulfilment (Pack → … → Close) is Admin-only; a Sales Head can only approve/reject/request.
  const canFulfil = usePermission('ORDER.EDIT');
  // Internal-only "Note" column (admin/SA assignment instruction) — hidden for RETAILER/FARMER.
  const userRole = useAuthStore((s) => s.user?.role);
  const isInternal = !!userRole && userRole !== 'RETAILER' && userRole !== 'FARMER';
  const canCancel = usePermission('ORDER.CANCEL') || canChangeStatus; // retailer / sales person also get ORDER.CANCEL
  // Whether this viewer DECIDES cancellation requests rather than raising them — mirrors
  // OrderCancelRequestService.needsApproval. Only decides what the dialog promises; the server is
  // still the authority.
  const canDecideCancel = usePermission('ORDER.CANCEL.APPROVE');
  const canDeliver = usePermission('ORDER.DELIVER'); // mark delivered — field action (SP/SH too)
  const canPay = usePermission('ORDER.PAY'); // retailer self-pay, or SP/SH on behalf
  const canUploadInvoice = usePermission('INVOICE.CREATE'); // the Tally invoice upload lives on the order page
  const { message } = useApp();
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const [status, setStatus] = useUrlString<string | undefined>('status', undefined);
  // Which half of PACKED — only meaningful beside status=PACKED, so a stray `lane` alone filters nothing.
  const [laneParam] = useUrlString<string | undefined>('lane', undefined);
  const lane = status === 'PACKED' && (laneParam === 'to-invoice' || laneParam === 'to-dispatch') ? laneParam : undefined;
  const [showClosed, setShowClosed] = useUrlBool('closed');
  /**
   * The date range is SEEDED FROM THE URL, not from empty.
   *
   * A dashboard card links here with the period it counted (`?from=…&to=…&status=…`). This state
   * used to start as [null, null] and never look at those params, so the status filter applied and
   * the DATES SILENTLY DID NOT: a card reading "Awaiting approval · 25" for the last 30 days opened
   * a list of every such order ever raised. The page looked right and the number was wrong, which is
   * the worst way for a drill-down to fail.
   */
  /*
   * The two ends are separate params ('from'/'to') because dashboard drill-downs already link that
   * way; the picker sees them as one range. Reading them through the hooks means the dates need no
   * mount-seeding and no re-sync effect — the URL is the state.
   */
  const [rangeFrom, setRangeFrom] = useUrlDate('from');
  const [rangeTo, setRangeTo] = useUrlDate('to');
  const range = useMemo<[Dayjs | null, Dayjs | null]>(() => [rangeFrom, rangeTo], [rangeFrom, rangeTo]);
  const setRange = useCallback((v: [Dayjs | null, Dayjs | null] | null) => {
    setRangeFrom(v?.[0] ?? null); setRangeTo(v?.[1] ?? null);
  }, [setRangeFrom, setRangeTo]);
  // Approving requires picking the fulfilment warehouse (+ live stock plan), so the list Approve button
  // opens the same ApproveOrderModal used on the detail/approval-queue pages.
  const [approveFor, setApproveFor] = useState<any>(null);
  const [payFor, setPayFor] = useState<any>(null);
  const [cancelFor, setCancelFor] = useState<{ id: string; orderNo: string; status: string } | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  /*
   * THE REST OF THE FILTERS THE SERVER ALREADY HONOURS.
   *
   * `GET /orders` accepts retailerId, salesPersonId, channel, minTotal, maxTotal and a `search` over
   * the order number, and applies every one of them in SQL — but the toolbar offered only the date
   * range, status and "show closed". `salesPersonId` was the plainest case: the page already READ it
   * from the URL and forwarded it, so a dashboard could filter to one rep and nobody on this screen
   * could see that it had, or clear it. URL-backed like the others, so Back restores them and a
   * filtered view is a link somebody can send.
   */
  const [search, setSearch] = useUrlString<string | undefined>('search', undefined);
  const [retailerId, setRetailerId] = useUrlString<string | undefined>('retailerId', undefined);
  const [salesPersonId, setSalesPersonId] = useUrlString<string | undefined>('salesPersonId', undefined);
  const [channel, setChannel] = useUrlString<string | undefined>('channel', undefined);
  const [minTotal, setMinTotal] = useUrlString<string | undefined>('minTotal', undefined);
  const [maxTotal, setMaxTotal] = useUrlString<string | undefined>('maxTotal', undefined);
  const from = range[0]?.format('YYYY-MM-DD');
  const to = range[1]?.format('YYYY-MM-DD');
  // A stage card on the dashboard links with a comma list; everything else sends one status.
  const multiStatuses = (status ?? '').split(',').map((x) => x.trim()).filter(Boolean);
  const isMultiStatus = multiStatuses.length > 1;
  // Deep-links like /orders?status=AWAITING_TRANSFER drive the status filter — re-synced on query
  // change, and so are the dates, so navigating between two dashboard cards re-filters both.
  /*
   * `status` and `showClosed` now READ from the query string themselves, so this only has to handle
   * the dates and the one deep-link rule below. It also keys on the status VALUE rather than on `sp`:
   * re-running on every param change made it recompute `showClosed` from the status whenever any
   * other filter moved, which silently pushed the "show closed" toggle back on the moment the user
   * turned it off.
   */
  const statusParam = sp.get('status');
  useEffect(() => {
    // A link that names a status means to show it even if it is a closed one — otherwise a
    // "Delivered · 7" card lands on a list that hides CLOSED and shows fewer than seven.
    if (statusParam) setShowClosed(true);
  }, [statusParam, setShowClosed]);
  /**
   * Select a lane by writing the URL, never by setting state alone.
   *
   * The effect above re-reads `status` FROM the query string, so a KPI card that only called
   * setStatus could be silently reverted the moment the params object changed — the card would flash
   * selected and the table would stay unfiltered. Writing the URL makes the two agree by construction,
   * and a chosen lane becomes a link somebody can send.
   */
  const selectLane = (next?: string, nextLane?: string) => {
    const q = new URLSearchParams(sp);
    if (next) q.set('status', next); else q.delete('status');
    // A plain status pick (the filter dropdown) is all of that status, so it drops a PACKED half.
    if (next && nextLane) q.set('lane', nextLane); else q.delete('lane');
    // Picking a lane re-filters the register, so it starts at its first page — this writes the URL
    // directly rather than through the filter hooks, so it has to drop `page` itself.
    q.delete('page');
    setSp(q, { replace: true });
  };

  /*
   * Which page of the register is on screen — URL-backed like every other filter, so Back returns to
   * the page you were working through instead of the top of the list, and "page 4 of the cancelled
   * ones" is a link somebody can send.
   */
  const [page, setPage] = useUrlNumber('page', 1);
  const [pageSize, setPageSize] = useUrlNumber('pageSize', DEFAULT_PAGE_SIZE);
  /** Narrowing the list must restart it: page 6 of an unfiltered register is past the end of most filters. */
  const firstPage = useCallback(() => setPage(1), [setPage]);

  // Server-side column sort (backend ORDER BY over the WHOLE register, not just the page on screen).
  const { sortState, onSortChange, sort } = useServerSort({ onChange: firstPage });

  /* The text box types faster than the server answers — debounce so a search is one request, not one
     per keystroke. Only the REQUEST waits; the input and the URL stay live under the cursor. */
  const searchQ = useDebounce(search, 400);

  /** Every filter the server applies, in one object — the table asks with it and the export exports it. */
  const listFilters = useMemo(() => ({
    status,
    lane,
    includeClosed: showClosed ? 'true' : undefined,
    from, to,
    search: searchQ || undefined,
    retailerId, salesPersonId, channel,
    minTotal: minTotal || undefined,
    maxTotal: maxTotal || undefined,
  }), [status, lane, showClosed, from, to, searchQ, retailerId, salesPersonId, channel, minTotal, maxTotal]);

  const { data, isLoading } = useQuery({
    queryKey: ['orders', listFilters, sort, page, pageSize],
    // `salesPersonId` is honoured by the orders controller but the list never forwarded it, so a
    // dashboard filtered to one rep opened everybody's orders.
    queryFn: () => request<any>({ url: '/orders', params: { ...listFilters, page, pageSize, sort } }),
    placeholderData: keepPreviousData,
  });
  /** The register's real size, from the server's COUNT — what the pager pages through. */
  const total = Number(data?.meta?.total ?? 0);

  /*
   * A page past the end is nobody's view. Filters reset the page where they are set (`firstPage`),
   * but a link can still arrive at `?page=9` for a register that now holds two pages, and that used
   * to render an empty table under a KPI band counting hundreds of orders. Landing on the LAST page
   * instead keeps the rows and the count agreeing.
   */
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  useEffect(() => {
    if (!isLoading && page > lastPage) setPage(lastPage);
  }, [isLoading, page, lastPage, setPage]);

  /*
   * KPI counts. Same scope + date range as the table, but NOT its status — see the band below and
   * OrderService.statusSummary. Kept as its own query so a lane click re-renders the table without
   * refetching (or collapsing) the cards.
   */
  /*
   * The band is scoped to the SAME rows as the table, minus `status` — the one filter it must ignore,
   * because the tiles ARE the status breakdown and a band narrowed to one lane would show that lane
   * and four zeroes. `/orders/summary` takes every other filter and applies them in the same SQL the
   * list uses, so filtering to one retailer now moves the tiles with the table instead of leaving
   * "₹1.8 Cr across 289 orders" sitting above eleven rows belonging to one dealer.
   */
  const summaryFilters = useMemo(() => {
    const { status: _status, lane: _lane, ...rest } = listFilters;
    return rest;
  }, [listFilters]);
  const { data: summary, isError: summaryFailed } = useQuery({
    queryKey: ['orders-summary', summaryFilters],
    queryFn: () => request<any>({ url: '/orders/summary', params: summaryFilters }),
    placeholderData: keepPreviousData,
  });
  const summaryReady = !summaryFailed && !!summary?.rows;
  const laneOf = (st: string, half?: string): { count: number; value: number; oldest: string | null; invoices?: number } =>
    (half ? summary?.packed?.[half] : (summary?.rows ?? []).find((r: any) => r.status === st)) ?? { count: 0, value: 0, oldest: null };
  /** Whole days a lane's oldest order has been sitting there — a queue's age is what makes it urgent. */
  const ageDays = (iso: string | null): number | null =>
    iso ? Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000)) : null;
  /*
   * The headline card must equal the rows it opens, so it follows the table's own CLOSED rule:
   * hidden by default, counted once "Show closed" is ticked.
   */
  const openTotals = useMemo(() => {
    let count = 0, value = 0;
    for (const r of (summary?.rows ?? []) as any[]) {
      if (!showClosed && r.status === 'CLOSED') continue;
      count += Number(r.count) || 0;
      value += Number(r.value) || 0;
    }
    return { count, value };
  }, [summary, showClosed]);

  // Resolve retailer + sales-person ids → human details (best-effort; gated by permission).
  const { data: retailers } = useQuery({
    queryKey: ['orders-retailer-map'],
    queryFn: () => requestAllPages<any>('/retailers'),
    enabled: canViewRetailers,
  });
  const retailerMap = useMemo(() => {
    const m = new Map<string, any>();
    for (const r of retailers?.data ?? []) m.set(String(r.id), r);
    return m;
  }, [retailers]);
  // The Retailer filter searches the server — a URL-selected dealer outside any page is fetched for its label.
  const retailerPick = useRetailerSearch({ key: 'orders-retailer-filter', enabled: canViewRetailers, selected: retailerId });

  const { data: users } = useQuery({
    queryKey: ['orders-user-map'],
    queryFn: () => requestAllPages<any>('/users'),
    enabled: canViewUsers,
  });
  const userMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of users?.data ?? []) m.set(String(u.id), u.fullName);
    return m;
  }, [users]);

  const rows = data?.data ?? [];

  // Inline status flip (Approve / Pack / Close / Cancel). The stock-allocating steps
  // (Dispatch / Deliver / Invoice) open the detail page instead of a bare flip.
  const change = useMutation({
    mutationFn: ({ id, status, notes }: { id: string; status: string; notes?: string }) =>
      request({ url: `/orders/${id}/status`, method: 'PATCH', data: notes?.trim() ? { status, notes: notes.trim() } : { status } }),
    onSuccess: (res: any) => {
      // A post-approval cancel by anyone who cannot DECIDE one comes back as a request, not a change.
      if (res?.requested) message.success('Cancellation sent to the Super Admin — the order is frozen until they decide');
      else message.success('Order status updated');
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['order-timeline'] });
      qc.invalidateQueries({ queryKey: ['order-detail'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to update status'),
  });

  // Cancel an in-progress consolidation — in-transit transfers return to source, order frees up.
  const cancelConsolidation = useMutation({
    mutationFn: (id: string) => request({ url: `/orders/${id}/consolidate/cancel`, method: 'POST' }),
    onSuccess: () => {
      message.success('Consolidation cancelled — stock returned to source warehouses');
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to cancel consolidation'),
  });

  const columns: DataTableColumn<any>[] = [
    /*
     * The order number OPENS the order. It used to be plain text, and nothing else on this page (or
     * on the retailer's Orders tab) linked to an order either — the row only expands. So a Sales
     * Person, who holds ORDER.VIEW and whose ORDER.PAY button lives on the order page, had no way to
     * reach it after placing the order: they could not collect a payment against their own sale.
     * OrderLink self-gates on ORDER.VIEW and never links for a customer role.
     */
    {
      key: 'orderNo', title: 'Order No', dataIndex: 'orderNo', sortable: true, alwaysVisible: true, width: 170,
      render: (v: string, o: any) => <OrderLink id={o.id}>{v}</OrderLink>,
    },
    {
      /*
       * Date and time as TWO unbreakable lines, not one wrapping string.
       *
       * `formatIst` returns "09 Sep 2026, 12:12 PM", which does not fit a 160px column — so it broke
       * at the last space and dropped a lone "PM" onto its own line under the date. A time is one
       * thing to a reader, not three words the layout may split wherever it runs out of room. The
       * date leads because that is what the column is sorted and filtered on.
       */
      key: 'orderDate', title: 'Date', dataIndex: 'orderDate', sortable: true, width: 150,
      render: (v: string) => (v ? (
        <div className="leading-tight">
          <div className="whitespace-nowrap text-content">{formatIstDate(v)}</div>
          <div className="whitespace-nowrap type-caption text-subtle">{formatIstTime(v)}</div>
        </div>
      ) : <span className="text-subtle">—</span>),
    },
    {
      // WIDTH IS LOAD-BEARING. This table is table-auto and every OTHER column declares a width,
      // so a width-less column here is not the flexible one — it is the only one the browser is
      // free to squeeze, and a long dealer name wrapped to four lines while the table itself
      // scrolls horizontally with room to spare. An explicit width plus `ellipsis` keeps the row
      // one line and puts the overflow where it belongs: the horizontal scroll.
      key: 'retailer', title: 'Retailer', alwaysVisible: true, width: 240, ellipsis: true,
      render: (_: any, o: any) => {
        // Just the company name — contact / code / city / mobile all live in the expanded row, so the main row
        // stays one line and its height is governed by the density control (Compact / Default / Spacious).
        //
        // Normal weight, NOT semibold. Retailer names are stored ALL-CAPS, and all-caps at 600 next to
        // 400-weight neighbours reads as a larger font even though every cell here is the same 14px —
        // caps have no descenders or x-height variation to size them against. The brand-green link
        // colour already marks this as the row's subject and as clickable; bold on top only shouted.
        const name = retailerMap.get(String(o.retailerId))?.companyName ?? o.companyName;
        // `ellipsis` auto-titles only a cell with a dataIndex; this one renders from two sources,
        // so it carries its own. A plain <span> stays INLINE, so the cell's ellipsis still draws.
        return name
          ? <span title={name}><RetailerLink id={o.retailerId}>{name}</RetailerLink></span>
          : <span className="text-subtle">#{o.retailerId}</span>;
      },
    },
    {
      key: 'products', title: 'Products', width: 220, ellipsis: true,
      /*
       * Most orders carry ONE line, so this column is mostly a product name — which is why it also
       * carries the QUANTITY: on a single-line order that is the only place the list says how much was
       * ordered at all.
       *
       * With several lines the old cell said "GreenGro Bio 500 ml +4" and stopped. Two things were
       * wrong with that. It hid the size of the order (four more of WHAT, how many units?), and it
       * counted PACK SIZES as products — four variants of one product is one thing to pick, not four.
       * So the second line names how many more there are in the right word, and totals the units.
       */
      render: (_: any, o: any) => {
        const ps: any[] = o.products ?? [];
        if (!ps.length) return <span className="text-subtle">—</span>;
        const lbl = (p: any) => { const v = variantText(p.packSize, p.uom, p.variantLabel); return `${p.name}${v ? ` ${v}` : ''}`; };
        const withQty = (p: any) => `${lbl(p)} × ${fmtQty(p.qty)}`;
        if (ps.length === 1) return <span className="truncate" title={withQty(ps[0])}>{withQty(ps[0])}</span>;
        const more = ps.length - 1;
        const units = ps.reduce((sum: number, p: any) => sum + (Number(p.qty) || 0), 0);
        /*
         * One product in several pack sizes is a VARIANT spread — calling those "products" overstates
         * what has to be picked. A MIXED order (say 3 sizes of one product and 2 of another) is
         * neither: "+4 products" would claim five products where there are two, so it counts ITEMS,
         * the word the order's own detail tab uses for its lines.
         */
        const word = new Set(ps.map((p) => p.name)).size === 1 ? 'variant' : 'item';
        return (
          <Tooltip label={ps.map(withQty).join(', ')}>
            <div className="min-w-0 leading-tight">
              <div className="truncate">{withQty(ps[0])}</div>
              <div className="type-caption text-subtle">+{more} more {word}{more === 1 ? '' : 's'} · {fmtQty(units)} units</div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      key: 'placedBy', title: 'Placed By', width: 200, alwaysVisible: true,
      // Truncate only the NAME; keep the role capsule fully visible (shrink-0) so it never gets clipped.
      render: (_: any, o: any) => (o.placedByName ? (
        <div className="flex items-center gap-1.5">
          {/* `createdBy` is the placer's user id (it rides in the list payload); a RETAILER login
              opens the dealer's own page instead of a staff page they do not have. */}
          <span className="min-w-0 truncate"><PersonLink id={o.createdBy} role={o.placedByRole} retailerId={o.retailerId}>{o.placedByName}</PersonLink></span>
          <RoleBadge role={o.placedByRole} />
        </div>
      ) : '—'),
    },
    { key: 'channel', title: 'Channel', dataIndex: 'channel', sortable: true, width: 110, defaultVisible: false },
    { key: 'subtotal', title: 'Subtotal', dataIndex: 'subtotal', sortable: true, align: 'right', width: 110, defaultVisible: false, render: (v: any) => inr2(v) },
    { key: 'discountAmt', title: 'Discount', dataIndex: 'discountAmt', sortable: true, align: 'right', width: 110, defaultVisible: false, render: (_: any, o: any) => inr2(effectiveDiscount(o)) },
    {
      key: 'grandTotal', title: 'Total', dataIndex: 'grandTotal', sortable: true, align: 'right', width: 130,
      render: (v: any, o: any) => (
        o.advanceCdStatus === 'ESTIMATED' && o.netGrandTotal != null ? (
          <Tooltip label={`Net payable ${inr2(o.netGrandTotal)} after advance cash discount (estimated · pending lock). Original ${inr2(v)}.`}>
            <div className="leading-tight">
              <b>{inr2(o.netGrandTotal)}</b>
              <div className="text-2xs text-subtle"><span className="line-through">{inr2(v)}</span></div>
            </div>
          </Tooltip>
        ) : <b>{inr2(v)}</b>
      ),
    },
    {
      key: 'status', title: 'Status', dataIndex: 'status', sortable: true, width: 120,
      render: (v: string, o: any) => {
        /*
         * The second pill says WHAT HAS ACTUALLY HAPPENED to the money — it used to say "Prepaid" for
         * three different situations, two of which were untrue:
         *   · an order with NO payment at all, purely because the system had auto-attached an advance-CD
         *     estimate at placement (an OFFER, not a payment — its own tooltip said "pending
         *     verification" while the pill said Prepaid);
         *   · an order where a few rupees of account credit had been auto-consumed — ₹404 against ₹8,653
         *     read as "Prepaid".
         * So the pill now follows the payments: covered → Prepaid, part → Part paid (with the figures),
         * nothing → the CD offer, named as an offer.
         */
        const cd = o.advanceCdStatus;
        const paid = Number(o.invoicePaid ?? 0);
        // What prepaying this order would cost: the net when a CD is on the table, else the full total.
        const target = Number(o.netGrandTotal ?? 0) > 0 ? Number(o.netGrandTotal) : Number(o.grandTotal ?? 0);
        const covered = paid > 0.01 && target > 0 && paid >= target - 0.01;
        const part = paid > 0.01 && !covered;
        const money = (n: any) => inr2(n);
        let pill: { label: string; color: string; tip: string } | null = null;
        if (covered) {
          pill = {
            label: 'Prepaid', color: cd === 'LOCKED' ? 'green' : 'default',
            tip: cd === 'LOCKED'
              ? `Prepaid — advance cash discount locked · net payable ${money(o.netGrandTotal)}.`
              : `Prepaid — ${money(paid)} received & accepted before billing.`,
          };
        } else if (part) {
          pill = {
            label: 'Part paid', color: 'blue',
            tip: `${money(paid)} of ${money(target)} received${Number(o.creditApplied) > 0.01 ? ` · ${money(o.creditApplied)} of it from account credit` : ''}.`,
          };
        } else if (cd === 'ESTIMATED') {
          pill = {
            label: `CD offer ${Number(o.advanceCdPct) > 0 ? `${Number(o.advanceCdPct)}%` : ''}`.trim(), color: 'gold',
            tip: `Nothing paid yet. Pay before billing to get ${money(o.advanceCdAmount)} off — ${money(o.netGrandTotal)} instead of ${money(o.grandTotal)}. The discount is confirmed only when the payment is verified.`,
          };
        }
        /*
         * PART-SHIPPED ORDERS LOOK IDENTICAL TO FINISHED ONES WITHOUT THIS.
         *
         * An order only leaves PACKED once EVERYTHING has shipped, so "PACKED" is worn equally by an
         * order waiting for a van and one that sent 70 of 100 a fortnight ago and is waiting on stock
         * for the rest. The balance is named with the same word the whole-order state uses — a parked
         * order is BACKORDER, a parked balance is "on backorder" — so one term covers both.
         */
        const onBackorder = o.partShipped ? Number(o.pendingQty ?? 0) : 0;
        return (
          <div className="flex flex-col items-start gap-1">
            <Pill color={STATUS_PILL[v] ?? 'default'}>{statusLabel(v)}</Pill>
            {onBackorder > 1e-6 && (
              <Tooltip label={`${fmtQty(o.shippedQty)} of ${fmtQty(Number(o.shippedQty ?? 0) + onBackorder)} units are packed or shipped. The balance is on backorder — it packs when stock covers it.`}>
                <span className="inline-flex"><Pill color="orange">{fmtQty(onBackorder)} on backorder</Pill></span>
              </Tooltip>
            )}
            {pill && (
              <Tooltip label={pill.tip}>
                <span className="inline-flex"><Pill color={pill.color as any}>{pill.label}</Pill></span>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    ...(isInternal ? [{
      // alwaysVisible so a newly-added conditional column can't be swallowed by the table's
      // once-initialised persisted column-visibility state (or a stale localStorage entry).
      key: 'approvalNote', title: 'Note', width: 200, ellipsis: true, alwaysVisible: true,
      render: (_: any, o: any) => (o.approvalNote ? (
        <Tooltip label={<>{o.approvalNote}{o.approvalNoteBy ? <><br />— {o.approvalNoteBy}</> : null}</>}>
          <span className="text-xs text-muted">📝 {o.approvalNote}</span>
        </Tooltip>
      ) : <span className="text-subtle">—</span>),
    } as DataTableColumn<any>] : []),
    {
      key: 'due', title: 'Payment', width: 150,
      render: (_: any, o: any) => {
        // A bare "by credit" read as the whole order paid from credit — PAWAR BROTHERS paid ₹2,500 by credit and
        // ₹20,619 by NEFT (E2E 15 Sep). Name the amount unless credit is all of what was paid.
        const creditOnly = Number(o.invoicePaid) - Number(o.creditApplied) <= 0.01;
        const credit = Number(o.creditApplied) > 0.01
          ? <Tooltip label={`${inr2(o.creditApplied)} settled from account credit${creditOnly ? '' : ` · ${inr2(Number(o.invoicePaid) - Number(o.creditApplied))} paid otherwise`}`}><span className="inline-flex"><Pill color="green">{creditOnly ? 'by credit' : `${inr2(o.creditApplied)} by credit`}</Pill></span></Tooltip>
          : null;
        if (!o.invoiceCount) return credit ?? <span className="text-subtle">—</span>;
        return (
          <span className="inline-flex items-center gap-1">
            {o.dueCleared ? (
              <Pill color="green"><Check className="size-3" /> Due cleared</Pill>
            ) : (
              <Tooltip label={`Paid ${inr2(o.invoicePaid)} of ${inr2(o.invoiceBilled)} — ${inr2(o.invoiceBalance)} outstanding`}>
                <span className="inline-flex"><Pill color="red">{inr2(o.invoiceBalance)} due</Pill></span>
              </Tooltip>
            )}
            {credit}
          </span>
        );
      },
    },
    {
      // Fixed width so the icon column lines up down the page even though rows carry different
      // numbers of actions — 5 squares + gaps is the worst case.
      key: 'actions', title: 'Actions', width: 190, align: 'right', alwaysVisible: true,
      render: (_: any, o: any) => {
        const stop = (e: any) => e.stopPropagation();
        // ICON-ONLY row actions — 32px squares, each with a tooltip and an aria-label carrying the
        // full wording. Labelled buttons needed a 320px column and still read as a wall of chrome on
        // a list whose point is the ORDER, not its buttons. Intent stays in the colour: green tint =
        // move the order forward, red = destructive, quiet grey = just look. Every destructive or
        // consequential one still goes through a Popconfirm or a modal, so an icon can't fire blind.
        const CLS = 'shrink-0';
        const IC = 'size-4';
        const act = (key: string, label: string, icon: any, onClick: (e: any) => void) => (
          <Tooltip key={key} label={label}>
            <Button variant="ghost" size="sm" className={CLS} aria-label={label} icon={icon} onClick={onClick} />
          </Tooltip>
        );
        const goDetail = (key: string, label: string, icon: any) =>
          act(key, label, icon, (e) => { stop(e); navigate(`/orders/${o.id}`); });
        const cancelable = canCancel && (PRE_DISPATCH_CANCELLABLE.includes(o.status) || (canFulfil && o.status === 'DISPATCHED'));
        const cancelBtn = cancelable ? (
          CANCEL_NEEDS_REASON.includes(o.status) ? (
            // Past approval a cancellation must say why, so the row opens the dialog instead of
            // confirming a request the server would reject for having no reason on it.
            <Tooltip key="cancel" label="Cancel order">
              <Button variant="text" size="sm" className={CLS} aria-label="Cancel order"
                icon={<Ban className={`${IC} text-danger-600 dark:text-danger-500`} />}
                onClick={(e) => { stop(e); setCancelFor({ id: String(o.id), orderNo: String(o.orderNo ?? ''), status: String(o.status) }); }} />
            </Tooltip>
          ) : (
            // Tooltip OUTSIDE, Popconfirm wrapping the Button directly — see the consolidation action below.
            <Tooltip key="cancel" label="Cancel order">
              <Popconfirm title="Cancel this order?" description="This cannot be undone."
                okText="Cancel order" okButtonProps={{ danger: true }}
                onConfirm={() => change.mutate({ id: String(o.id), status: 'CANCELLED' })} onCancel={stop}>
                <Button variant="text" size="sm" className={CLS} aria-label="Cancel order" icon={<Ban className={`${IC} text-danger-600 dark:text-danger-500`} />} onClick={stop} />
              </Popconfirm>
            </Tooltip>
          )
        ) : null;
        const flipBtn = (key: string, label: string, icon: any, to: string) => (
          <Tooltip key={key} label={label}>
            <Popconfirm title={`${label}?`} okText={label}
              onConfirm={() => change.mutate({ id: String(o.id), status: to })} onCancel={stop}>
              <Button variant="ghost" size="sm" className={CLS} aria-label={label} icon={icon} onClick={stop} />
            </Popconfirm>
          </Tooltip>
        );
        // Close — allowed only once the invoice balance is cleared (backend-enforced); disabled with the
        // outstanding amount otherwise.
        const closeBtn = () => (o.invoiceCount === 0 || o.dueCleared) ? flipBtn('cl', 'Close', <Check className={IC} />, 'CLOSED') : (
          <Tooltip key="cl" label={`Clear the ${inr2(o.invoiceBalance)} outstanding balance before closing`}>
            <span className="inline-flex shrink-0" onClick={stop}><Button variant="ghost" size="sm" className={CLS} aria-label="Close order" icon={<Check className={IC} />} disabled /></span>
          </Tooltip>
        );
        const approveBtn = act('ap', 'Approve', <CheckCircle2 className={IC} />, (e) => { stop(e); setApproveFor(o); });
        const isShort = o.stockComplete === false;

        // Collected as (rank, element) and SORTED before render — see ACTION_ORDER. The status
        // branches below decide only WHICH actions a row gets, never where they sit.
        const acts: { r: number; el: any }[] = [];
        const add = (name: keyof typeof ACTION_ORDER | string, el: any) => { if (el) acts.push({ r: ACTION_ORDER[name] ?? 99, el }); };

        // Pay — retailer self-pay (or SP/SH on behalf) whenever the order still has a due.
        if (canPay && Number(o.invoiceBalance) > 0.01 && !['DRAFT', 'CANCELLED', 'REJECTED', 'CLOSED'].includes(o.status)) {
          add('pay',
            <Tooltip key="pay" label={`Pay ${inr2(o.invoiceBalance)}`}>
              <Button variant="ghost" size="sm" className={CLS} aria-label={`Pay ${inr2(o.invoiceBalance)}`} icon={<Wallet className={IC} />} onClick={(e) => { stop(e); setPayFor(o); }} />
            </Tooltip>,
          );
        }
        // Mark-delivered is a field action — available to SP/SH and admins; opens the detail to record POD.
        if (canDeliver && o.status === 'DISPATCHED') {
          add('deliver', goDetail('dl', 'Deliver', <Flag className={IC} />));
        }
        // Packed goods still waiting on their Tally invoice: the next act is the upload, so the order page opens with its
        // upload dialog already up (`?upload=invoice`). Offered to whoever uploads invoices, whatever else they can do with the order.
        const awaitingInvoice = o.status === 'PACKED' && o.awaitingInvoice === true;
        if (canUploadInvoice && awaitingInvoice) {
          add('invoice', act('ui', 'Upload the tax invoice', <Upload className={IC} />, (e) => { stop(e); navigate(`/orders/${o.id}?upload=invoice`); }));
        }
        if (canChangeStatus) {
          if (o.status === 'PLACED') { add('approve', approveBtn); add('cancel', cancelBtn); }
          else if (o.status === 'AWAITING_PRODUCTION') {
            if (!isShort) {
              if (o.everApproved) { if (canFulfil) add('pick', goDetail('pk', 'Pick & pack', <Package className={IC} />)); add('cancel', cancelBtn); }
              else { add('approve', approveBtn); add('cancel', cancelBtn); }
            } else add('cancel', cancelBtn);
          }
          else if (o.status === 'IN_PRODUCTION') add('cancel', cancelBtn);
          // AWAITING_TRANSFER: held while consolidating — Status column shows it; keep Actions compact
          // (cancel the consolidation for admins + the view button).
          else if (o.status === 'AWAITING_TRANSFER') {
            if (canFulfil) add('cancelConsolidation',
              // Popconfirm wraps the Button DIRECTLY (not through Tooltip): Radix chains the confirm-toggle
              // onto the Button's own onClick, so the button's stopPropagation (which blocks row-nav) no
              // longer also swallows the popup trigger. Tooltip sits outside.
              <Tooltip key="cc" label="Cancel consolidation">
                <Popconfirm title="Cancel consolidation?" description="In-transit stock returns to source warehouses."
                  okText="Cancel consolidation" okButtonProps={{ danger: true }}
                  onConfirm={() => cancelConsolidation.mutate(String(o.id))} onCancel={stop}>
                  <Button variant="text" size="sm" className={CLS} aria-label="Cancel consolidation" icon={<ArrowLeftRight className={`${IC} text-danger-600 dark:text-danger-500`} />} onClick={stop} />
                </Popconfirm>
              </Tooltip>,
            );
          }
          else if (o.status === 'APPROVED') {
            if (!isShort && canFulfil) add('pick', goDetail('pk', 'Pick & pack', <Package className={IC} />));
            add('cancel', cancelBtn);
          }
          // Not "Dispatch" while the invoice is missing — the server would refuse it (INVOICE_REQUIRED).
          else if (canFulfil && o.status === 'PACKED') { if (!awaitingInvoice) add('dispatch', goDetail('dp', 'Dispatch', <Truck className={IC} />)); add('cancel', cancelBtn); }
          else if (canFulfil && o.status === 'DISPATCHED') add('cancel', cancelBtn); // Deliver handled above
          else if (canFulfil && o.status === 'DELIVERED') {
            if (o.invoiceCount > 0) add('close', closeBtn());
            else add('invoice', goDetail('iv', 'Invoice', <FileCheck2 className={IC} />));
          }
          else if (canFulfil && o.status === 'INVOICED') add('close', closeBtn());
        } else {
          add('cancel', cancelBtn);
        }
        acts.sort((a, b) => a.r - b.r);
        return (
          <div className="flex flex-nowrap items-center justify-end gap-1 whitespace-nowrap" onClick={stop}>
            {acts.map((a) => a.el)}
            {/* View is the one action every row has, so it holds the last slot on every row — the
                right edge of the column is always the same button. */}
            <Tooltip label="Open the order">
              <Button variant="text" size="sm" className={`${CLS} text-muted! hover:text-content!`} aria-label="Open the order" icon={<Eye className={IC} />} onClick={(e) => { stop(e); navigate(`/orders/${o.id}`); }} />
            </Tooltip>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      {/* Header row — page title + primary actions, above the filters. */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <h1 className="type-page-title text-content" style={{ margin: 0 }}>Orders</h1>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {canExportOrders && (
            <Button size="sm" icon={<Download className="size-4" />} onClick={() => setExportOpen(true)}>Export</Button>
          )}
          {canCreateOrder && (
            <Button variant="primary" size="sm" icon={<Plus className="size-4" />} onClick={() => navigate('/orders/new')}>New order</Button>
          )}
        </div>
      </div>
      {/*
        * KPI band — the queue, not a summary of whatever the table is currently filtered to.
        *
        * Two rules shape this. (1) The cards are counted server-side over the same scope and date
        * range as the table but WITHOUT its status filter, so picking a lane narrows the rows while
        * the other cards keep answering "what else is waiting". (2) Each card stands for exactly ONE
        * status, so its click reproduces the number printed on it — a card covering two lanes could
        * not, because the status filter shows one value at a time and the state would go invisible.
        */}
      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <StatCard
          label={showClosed ? 'All orders' : 'Open orders'}
          value={summaryReady ? inr(openTotals.value) : '—'}
          hint={summaryReady
            ? `${openTotals.count} order${openTotals.count === 1 ? '' : 's'}`
              + (showClosed ? '' : ' · closed hidden')
            : 'Could not load'}
          tone="neutral"
          icon={<ClipboardList className="size-4" />}
          active={!status}
          // Always clickable: "no status filter" is a state you can always return to, even at zero.
          onClick={() => selectLane(undefined)}
        />
        {KPI_LANES.map((l) => {
          const row = laneOf(l.status, l.lane);
          const age = ageDays(row.oldest);
          const isActive = status === l.status && (l.lane ? lane === l.lane : !lane);
          /*
           * The uploads still owed — the Accounts badge's number. It differs from the order count only when one
           * per-shipment order has two packed shipments waiting, so it is printed only then.
           */
          const owed = l.lane === 'to-invoice' && Number(row.invoices ?? 0) !== row.count
            ? ` · ${row.invoices} invoice${row.invoices === 1 ? '' : 's'} to upload` : '';
          return (
            <StatCard
              key={l.lane ?? l.status}
              label={l.label}
              value={summaryReady ? inr(row.value) : '—'}
              hint={summaryReady
                ? `${row.count} order${row.count === 1 ? '' : 's'}${owed}${age != null && row.count > 0 ? ` · oldest ${age}d` : ''}`
                : 'Could not load'}
              tone={l.tone}
              icon={l.icon}
              active={isActive}
              /*
               * A card counting nothing is not a filter — clicking it could only ever produce an
               * empty table, so it is left inert (a div, no hover lift) rather than offered.
               */
              onClick={summaryReady && row.count > 0
                ? () => (isActive ? selectLane(undefined) : selectLane(l.status, l.lane))
                : undefined}
            />
          );
        })}
      </div>
      <TwDataTable
        storageKey="orders-list"
        rowKey="id"
        loading={isLoading}
        dataSource={rows}
        columns={columns}
        sortState={sortState}
        onSortChange={onSortChange}
        expandable={{
          expandRowByClick: true,
          expandedRowRender: (o: any) => (
            <OrderExpand order={o} retailer={retailerMap.get(String(o.retailerId))} salesPersonName={userMap.get(String(o.salesPersonId))} />
          ),
        }}
        /* Server-paged: the table renders the page it is handed and reports moves back up, so the
           pager counts the whole register (302 orders → 13 pages) rather than the slice in memory. */
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (t) => `${t.toLocaleString('en-IN')} order${t === 1 ? '' : 's'}`,
          onChange: (p, ps) => { setPage(ps === pageSize ? p : 1); setPageSize(ps); },
        }}
        emptyText="No orders"
        toolbarLeft={
          <>
            {/* FILTERS IN COLUMN ORDER: order no → date → retailer → sales person → channel →
                total → status. The toolbar reads left to right the way the table does, so finding
                the control for a column is a matter of looking under it rather than hunting. */}
            <Input
              allowClear
              value={search ?? ''}
              onChange={(e) => { setSearch(e.target.value || undefined); firstPage(); }}
              placeholder="Order no / retailer"
              prefix={<Search className="size-4 text-subtle" />}
              style={{ width: 170 }}
              aria-label="Search by order number"
            />
            <DatePicker.RangePicker disabledDate={disableFuture}
              value={range as any}
              onChange={(r) => { setRange((r as any) ?? [null, null]); firstPage(); }}
              allowEmpty={[true, true]}
            />
            {/* Sales-person options come from the map the page ALREADY fetches to put names on the rows;
                retailers are searched on the server (too many for one page). Hidden when the viewer cannot
                read that master: a picker of names somebody has no permission to see is a leak, not a filter. */}
            {canViewRetailers && (
              <Select
                allowClear showSearch optionFilterProp="searchLabel" filterOption={false} onSearch={retailerPick.onSearch}
                placeholder="Retailer" style={{ width: 190 }} popupMatchSelectWidth={false}
                value={retailerId} onChange={(v) => { setRetailerId((v as string) || undefined); firstPage(); }}
                options={retailerPick.rows.map((r: any) => ({
                  value: String(r.id),
                  searchLabel: `${r.companyName ?? ''} ${r.code ?? ''}`.trim(),
                  label: (
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="truncate">{r.companyName ?? `#${r.id}`}</span>
                      {r.code && <CodeChip hue={entityHue(r.code)}>{r.code}</CodeChip>}
                    </span>
                  ),
                }))}
              />
            )}
            {canViewUsers && (
              <Select
                allowClear showSearch optionFilterProp="searchLabel"
                placeholder="Sales person" style={{ width: 180 }} popupMatchSelectWidth={false}
                value={salesPersonId} onChange={(v) => { setSalesPersonId((v as string) || undefined); firstPage(); }}
                options={(users?.data ?? [])
                  .filter((u: any) => ['SALES_PERSON', 'SALES_HEAD'].includes(String(u.roleCode)))
                  .map((u: any) => ({
                    value: String(u.id),
                    searchLabel: u.fullName ?? '',
                    label: (
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="truncate">{u.fullName ?? `#${u.id}`}</span>
                        <RoleBadge role={u.roleCode} />
                      </span>
                    ),
                  }))}
              />
            )}
            {/* The channel list and its colours come from the shared CHANNELS map that ChannelTag
                draws the column with, so the picker and the row show one definition of "Web". */}
            <Select
              allowClear placeholder="Channel" style={{ width: 158 }} popupMatchSelectWidth={false}
              value={channel} onChange={(v) => { setChannel((v as string) || undefined); firstPage(); }}
              options={Object.entries(CHANNELS).map(([value, c]) => ({
                value,
                searchLabel: c.label,
                label: <ChannelTag channel={value} />,
              }))}
            />
            {/* The TOTAL column's own filter — `grandTotal >= / <=` on the server. Two boxes rather
                than one range control because either end is useful alone ("everything over ₹50,000"
                is the common ask, with no upper bound to state). */}
            <span className="inline-flex items-center gap-1">
              <InputNumber
                min={0} placeholder="Min ₹" style={{ width: 108 }}
                value={minTotal === undefined ? undefined : Number(minTotal)}
                onChange={(v) => { setMinTotal(v == null || v === ('' as any) ? undefined : String(v)); firstPage(); }}
                aria-label="Minimum order total"
              />
              <span className="text-subtle">→</span>
              <InputNumber
                min={0} placeholder="Max ₹" style={{ width: 108 }}
                value={maxTotal === undefined ? undefined : Number(maxTotal)}
                onChange={(v) => { setMaxTotal(v == null || v === ('' as any) ? undefined : String(v)); firstPage(); }}
                aria-label="Maximum order total"
              />
            </span>
            {/* A dashboard stage card links here with SEVERAL statuses at once
                (?status=DRAFT,PLACED,ON_HOLD). The single-select cannot show that, and rendering it
                as an unmatched value would leave the box blank while the table stayed narrowed —
                a filter nobody can see and nobody can clear. So a multi-status link becomes a
                labelled, dismissable chip instead. */}
            {isMultiStatus ? (
              <Tag tone="brand" className="inline-flex items-center gap-1.5 py-1.5">
                {multiStatuses.length} statuses: {multiStatuses.map(statusLabel).join(', ')}
                <button
                  type="button" aria-label="Clear status filter" onClick={() => selectLane(undefined)}
                  className="ml-0.5 rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10"
                >
                  <X className="size-3.5" />
                </button>
              </Tag>
            ) : (
              /* THE SAME PILL THE COLUMN SHOWS. The list paints every status as a coloured pill and
                 then asked you to pick one from a list of flat uppercase text, so the two halves of
                 the same fact looked like different things and the colour you were hunting for had
                 no counterpart in the picker. `searchLabel` keeps type-to-search working now that
                 the label is a node — the same shape `bankAcctOption` uses.
                 (Plain block comment, not a braced JSX one: this is a ternary BRANCH, an
                 expression position, not JSX children.) */
              <Select
                placeholder="Filter status" allowClear style={{ width: 200 }} popupMatchSelectWidth={false}
                showSearch optionFilterProp="searchLabel"
                value={status} onChange={(v) => selectLane(v as string | undefined)}
                options={FILTER_STATUSES.map((s) => ({
                  value: s,
                  searchLabel: statusLabel(s),
                  label: <Pill color={STATUS_PILL[s] ?? 'default'}>{statusLabel(s)}</Pill>,
                }))}
              />
            )}
            <Checkbox checked={showClosed} onCheckedChange={(checked) => { setShowClosed(checked); firstPage(); }} label="Show closed" />
          </>
        }
      />
      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title="Export orders"
        description="The order register for the retailers and warehouses in your scope."
        baseUrl="/orders/export"
        /* EVERY filter the page is showing, not just three of them — the file is the view.
           The dialog's own period picker is hidden (`hidePeriod`): it asked for a date range the
           toolbar right behind it had already set, so the two could disagree and the dialog won,
           exporting a period nobody had chosen. With it gone the page's `from`/`to` pass straight
           through `baseParams`, and the server honours the rest (retailer, sales person, channel,
           amount range, order-no search) through the same DTO the list uses. */
        hidePeriod
        /* `sort` is state of its own, not part of `listFilters`, so it never rode along and the file
           came back in the server's default order — the reader sorts the register by Date or Total,
           exports it, and gets something else. The file is the view; the column order is part of it. */
        baseParams={{ ...listFilters, sort }}
      />
      <ApproveOrderModal
        open={!!approveFor}
        orderId={approveFor ? String(approveFor.id) : null}
        orderNo={approveFor?.orderNo}
        defaultWarehouseId={approveFor?.dispatchWarehouseId}
        onClose={() => setApproveFor(null)}
        onApproved={() => {
          qc.invalidateQueries({ queryKey: ['orders'] });
          qc.invalidateQueries({ queryKey: ['badges'] });
          setApproveFor(null);
        }}
      />
      {/* Cancelling a post-approval order — the reason is mandatory server-side and is what the dealer
          is told, so it is collected here rather than discovered as a 400. */}
      <Modal
        open={!!cancelFor}
        onOpenChange={(open) => { if (!open) { setCancelFor(null); setCancelReason(''); } }}
        title={cancelFor ? `Cancel order ${cancelFor.orderNo}?` : 'Cancel order'}
        width={460}
        footer={
          <>
            <Button variant="text" onClick={() => { setCancelFor(null); setCancelReason(''); }}>Keep the order</Button>
            <Button variant="danger" disabled={!cancelReason.trim()} loading={change.isPending}
              onClick={() => cancelFor && change.mutate({ id: cancelFor.id, status: 'CANCELLED', notes: cancelReason.trim() },
                { onSuccess: () => { setCancelFor(null); setCancelReason(''); } })}>
              Cancel order
            </Button>
          </>
        }
      >
        {!canDecideCancel && (
          <Alert type="warning" style={{ marginBottom: 12 }}
            title="A Super Admin decides this."
            description="Past approval the warehouse has been routed the work and a proforma is out, so cancelling is asked for, not done. Your reason is what they rule on, and the order is frozen until they decide." />
        )}
        <Textarea rows={3} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} autoFocus
          placeholder="Why is this order being cancelled? (mandatory — recorded on the order history)" />
      </Modal>
      {payFor && (
        <PayOrderModal open={!!payFor} orderId={String(payFor.id)} orderNo={payFor.orderNo} onClose={() => setPayFor(null)} />
      )}
    </div>
  );
}
